import { useMemo } from 'react';
import { formatDate } from '../../../utils/dateUtils';
import { useAuth } from '../../../context/AuthContext';

export const usePcgsColumns = () => {
  const { user } = useAuth();

  const columns = useMemo(() => [
    {
      field: 'pcgs_number',
      headerName: 'PCGS Number',
      type: 'text',
      sortable: true,
      filterable: true,
      editable: false, // Don't allow editing the primary key
      width: 120,
      pinned: true,
      renderCell: (item) => (
        <span className="font-medium text-gray-900">{item.pcgs_number}</span>
      )
    },
    {
      field: 'country',
      headerName: 'Country',
      type: 'text',
      sortable: true,
      filterable: true,
      editable: true,
      width: 150
    },
    {
      field: 'year',
      headerName: 'Year',
      type: 'text',
      sortable: true,
      filterable: true,
      editable: true,
      width: 100,
      sortComparator: (v1, v2) => {
        // Handle null/undefined values
        if (!v1 && !v2) return 0;
        if (!v1) return -1;
        if (!v2) return 1;

        // Helper to check if a string contains numbers
        const hasNumbers = str => /\d/.test(str);
        
        // If both values have numbers, compare the numeric parts
        if (hasNumbers(v1) && hasNumbers(v2)) {
          const getYear = str => {
            const match = str.match(/\d+/);
            return match ? parseInt(match[0], 10) : 0;
          };
          return getYear(v1) - getYear(v2);
        }
        
        // If only one has numbers, prioritize numeric years
        if (hasNumbers(v1)) return -1;
        if (hasNumbers(v2)) return 1;
        
        // If neither has numbers, sort alphabetically
        return v1.localeCompare(v2);
      }
    },
    {
      field: 'denomination',
      headerName: 'Denomination',
      type: 'text',
      sortable: true,
      filterable: true,
      editable: true,
      width: 150
    },
    {
      field: 'mint_mark',
      headerName: 'Mint Mark',
      type: 'text',
      sortable: true,
      filterable: true,
      editable: true,
      width: 100
    },
    {
      field: 'assayer',
      headerName: 'Assayer',
      type: 'text',
      sortable: true,
      filterable: true,
      editable: true,
      width: 120
    },
    {
      field: 'reference_number',
      headerName: 'Reference #',
      type: 'text',
      sortable: true,
      filterable: true,
      editable: true,
      width: 120
    },
    {
      field: 'variety',
      headerName: 'Variety',
      type: 'text',
      sortable: true,
      filterable: true,
      editable: true,
      width: 150
    },
    {
      field: 'minor_variety',
      headerName: 'Minor Variety',
      type: 'text',
      sortable: true,
      filterable: true,
      editable: true,
      width: 150
    },
    {
      field: 'die_variety',
      headerName: 'Die Variety',
      type: 'text',
      sortable: true,
      filterable: true,
      editable: true,
      width: 150
    },
    {
      field: 'metal',
      headerName: 'Metal',
      type: 'text',
      sortable: true,
      filterable: true,
      editable: true,
      width: 120
    },
    {
      field: 'composition',
      headerName: 'Composition',
      type: 'number',
      sortable: true,
      filterable: true,
      editable: true,
      width: 120,
      renderCell: (item) => {
        const num = parseFloat(item.composition);
        return !isNaN(num) ? num.toFixed(3) : '-';
      }
    },
    {
      field: 'weight',
      headerName: 'Weight (g)',
      type: 'number',
      sortable: true,
      filterable: true,
      editable: true,
      width: 100,
      renderCell: (item) => {
        const num = parseFloat(item.weight);
        return !isNaN(num) ? num.toFixed(3) : '-';
      }
    },
    {
      field: 'diameter',
      headerName: 'Diameter (mm)',
      type: 'number',
      sortable: true,
      filterable: true,
      editable: true,
      width: 120,
      renderCell: (item) => {
        const num = parseFloat(item.diameter);
        return !isNaN(num) ? num.toFixed(3) : '-';
      }
    },
    {
      field: 'edge',
      headerName: 'Edge',
      type: 'text',
      sortable: true,
      filterable: true,
      editable: true,
      width: 120
    },
    {
      field: 'mintage',
      headerName: 'Mintage',
      type: 'text',
      sortable: true,
      filterable: true,
      editable: true,
      width: 150
    },
    {
      field: 'updated_at',
      headerName: 'Last Updated',
      type: 'date',
      sortable: true,
      filterable: false,
      editable: false,
      width: 150,
      renderCell: (item) => formatDate(item.updated_at, { user })
    }
  ], [user]);

  return columns;
};

export const usePcgsRows = (records) => {
  return useMemo(() => {
    if (!records || !Array.isArray(records)) return [];
    
    return records.map(record => ({
      id: record.pcgs_number, // Use pcgs_number as the unique ID
      ...record,
      // Ensure null values are displayed as empty strings for editing
      country: record.country || '',
      year: record.year || '',
      denomination: record.denomination || '',
      mint_mark: record.mint_mark || '',
      assayer: record.assayer || '',
      reference_number: record.reference_number || '',
      variety: record.variety || '',
      minor_variety: record.minor_variety || '',
      die_variety: record.die_variety || '',
      metal: record.metal || '',
      edge: record.edge || '',
      mintage: record.mintage || ''
    }));
  }, [records]);
};
